import { mulberry32, seedFromString } from '../utils/seededRandom.ts';
import { GATE_CLEARANCE } from './maze/buildGrounds.ts';
import { CELL_SIZE } from './maze/grid.ts';
import { generateMaze } from './maze/generateMaze.ts';
import type { LevelSpec, Profile } from './profile/types.ts';

/** The prefix of every generated level and objective id; authored ids may not use it. */
export const ENDLESS_ID_PREFIX = 'endless-';

const ENDLESS_ID = /^endless-([1-9]\d*)$/;

export function endlessLevelId(index: number): string {
  return `${ENDLESS_ID_PREFIX}${index}`;
}

/** The 1-based index in a generated level's id, or null for any other id. */
export function endlessIndex(levelId: string): number | null {
  const match = ENDLESS_ID.exec(levelId);
  return match === null ? null : Number(match[1]);
}

// Memoised so a given (profile, N) is always the same object: App memoises the built
// geometry on the level's identity, and CarryController looks levels up every frame.
const cache = new WeakMap<Profile, Map<number, LevelSpec>>();

/** Generated maze N of a profile that declares `endless`; null for one that does not. */
export function endlessLevel(profile: Profile, index: number): LevelSpec | null {
  const endless = profile.endless;
  if (endless === undefined) return null;

  let levels = cache.get(profile);
  if (levels === undefined) {
    levels = new Map();
    cache.set(profile, levels);
  }
  const known = levels.get(index);
  if (known !== undefined) return known;

  const random = mulberry32(seedFromString(`endless-size:${endless.seed}:${index}`));
  const span = endless.maxCells - endless.minCells + 1;
  const columns = endless.minCells + Math.floor(random() * span);
  const rows = endless.minCells + Math.floor(random() * span);
  const seed = `${endless.seed}:${index}`;
  const maze = generateMaze(seed, columns, rows);

  const id = endlessLevelId(index);
  const level: LevelSpec = {
    id,
    spawn: {
      position: {
        x: (-columns * CELL_SIZE) / 2 + (maze.entryColumn + 0.5) * CELL_SIZE,
        z: (rows * CELL_SIZE) / 2 - GATE_CLEARANCE,
      },
      yaw: 0,
    },
    rooms: [],
    doors: [],
    objects: [],
    objectives: [{ id: `${id}-exit`, kind: 'reach-exit', description: endless.objective }],
    grounds: { seed, columns, rows },
  };
  levels.set(index, level);
  return level;
}
